// components/QuickQuoteModal.jsx
import React, { useState, useEffect } from "react";
import {
  X,
  Plus,
  Trash2,
  Package,
  Search,
  DollarSign,
  Loader,
  Send,
  Save,
  Calendar,
  FileText,
  CheckCircle,
} from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const QuickQuoteModal = ({ opportunity, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [services, setServices] = useState([]);
  const [serviceSearch, setServiceSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    customerName: "",
    customerEmail: "",
    customerCompany: "",
    customerPhone: "",
    validUntil: "",
    paymentTerms: "Payment due within 30 days of invoice",
    termsAndConditions:
      "Standard terms and conditions apply. Prices exclude VAT unless stated.",
    notes: "",
    discountAmount: 0,
    discountType: "fixed",
    currency: "GBP",
  });

  const [lineItems, setLineItems] = useState([]);

  useEffect(() => {
    initialize();
  }, []);

  const initialize = async () => {
    try {
      setLoading(true);
      const [initRes, servicesRes] = await Promise.all([
        apiService.initializeDigitalQuoteFromOpportunity(opportunity.id),
        apiService.getQuoteServices({ isActive: true, limit: 200 }),
      ]);

      if (initRes.success) {
        setFormData((prev) => ({ ...prev, ...initRes.data }));
      }
      setServices(servicesRes.data || []);
    } catch (err) {
      console.error("Init failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const addService = (service) => {
    const existing = lineItems.find((li) => li.serviceId === service.id);
    if (existing) {
      setLineItems(
        lineItems.map((li) =>
          li.serviceId === service.id
            ? { ...li, quantity: li.quantity + 1 }
            : li,
        ),
      );
      return;
    }

    // Build term pricing tiers from the service catalogue entry.
    // The Service model may expose `termPrices` as [{termMonths, unitPrice}].
    // If present we pre-select the shortest term; otherwise fall back to flat price.
    const hasTiers =
      Array.isArray(service.termPrices) && service.termPrices.length > 0;

    const sortedTiers = hasTiers
      ? [...service.termPrices].sort((a, b) => a.termMonths - b.termMonths)
      : [];

    const defaultTier = sortedTiers[0] || null;

    setLineItems([
      ...lineItems,
      {
        serviceId: service.id,
        name: service.name,
        description: service.description,
        category: service.category,
        billingCycle: service.billingCycle,
        quantity: service.defaultQuantity || 1,
        unitPrice: defaultTier
          ? parseFloat(defaultTier.unitPrice)
          : parseFloat(service.unitPrice),
        discount: 0,
        taxRate: parseFloat(service.taxRate) || 20,
        termPrices: hasTiers ? sortedTiers : null,
        termMonths: defaultTier ? parseInt(defaultTier.termMonths) : null,
      },
    ]);
  };

  const addCustomItem = () => {
    setLineItems([
      ...lineItems,
      {
        serviceId: null,
        name: "Custom Item",
        description: "",
        category: "other",
        billingCycle: "one_time",
        quantity: 1,
        unitPrice: 0,
        discount: 0,
        taxRate: 20,
      },
    ]);
  };

  const updateLineItem = (index, field, value) => {
    setLineItems(
      lineItems.map((li, i) => (i === index ? { ...li, [field]: value } : li)),
    );
  };

  // Merge multiple fields at once — avoids stale-state bugs when two fields
  // must change together (e.g. termMonths + unitPrice from the term dropdown).
  const updateLineItemFields = (index, fields) => {
    setLineItems(
      lineItems.map((li, i) => (i === index ? { ...li, ...fields } : li)),
    );
  };

  const removeLineItem = (index) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let taxAmount = 0;
    let monthlyRecurring = 0;

    lineItems.forEach((li) => {
      const qty = parseInt(li.quantity) || 0;
      // Resolve unit price from the selected term tier if available
      let price = parseFloat(li.unitPrice) || 0;
      if (
        li.termMonths &&
        Array.isArray(li.termPrices) &&
        li.termPrices.length > 0
      ) {
        const tier = li.termPrices.find(
          (t) => parseInt(t.termMonths) === parseInt(li.termMonths),
        );
        if (tier) price = parseFloat(tier.unitPrice) || price;
      }
      const discount = parseFloat(li.discount) || 0;
      const taxRate = parseFloat(li.taxRate) || 0;
      const lineSub = qty * price - discount;
      const lineTax = lineSub * (taxRate / 100);
      subtotal += lineSub;
      taxAmount += lineTax;
      if (li.billingCycle === "monthly") monthlyRecurring += lineSub;
    });

    let effectiveDiscount = 0;
    if (formData.discountType === "percentage") {
      effectiveDiscount =
        subtotal * (parseFloat(formData.discountAmount) / 100);
    } else {
      effectiveDiscount = parseFloat(formData.discountAmount) || 0;
    }

    const total = subtotal + taxAmount - effectiveDiscount;

    return {
      subtotal,
      taxAmount,
      effectiveDiscount,
      total,
      monthlyRecurring,
    };
  };

  const totals = calculateTotals();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: formData.currency || "GBP",
    }).format(amount || 0);
  };

  const handleSave = async (andSend = false) => {
    if (lineItems.length === 0) {
      alert("Please add at least one service to the quote");
      return;
    }
    if (!formData.title || !formData.customerName || !formData.customerEmail) {
      alert("Title, customer name and email are required");
      return;
    }

    try {
      setSaving(true);
      const payload = {
        opportunityId: opportunity.id,
        leadId: opportunity.leadId,
        accountId: opportunity.accountId,
        contactId: opportunity.contactId,
        ...formData,
        lineItems,
      };

      const res = await apiService.createDigitalQuote(payload);
      if (res.success && andSend) {
        await apiService.sendDigitalQuote(res.data.id);
      }

      onSuccess?.(res.data);
      onClose();
    } catch (err) {
      console.error("Save failed:", err);
      alert(
        "Failed to save quote: " + (err.response?.data?.message || err.message),
      );
    } finally {
      setSaving(false);
    }
  };

  const filteredServices = services.filter((s) => {
    const matchesSearch =
      !serviceSearch ||
      s.name.toLowerCase().includes(serviceSearch.toLowerCase()) ||
      s.description?.toLowerCase().includes(serviceSearch.toLowerCase()) ||
      s.sku?.toLowerCase().includes(serviceSearch.toLowerCase());
    const matchesCategory =
      categoryFilter === "all" || s.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const categories = ["all", ...new Set(services.map((s) => s.category))];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        className={`rounded-lg border w-full max-w-6xl max-h-[90vh] flex flex-col ${
          isDarkMode
            ? "bg-[#13161B] border-gray-700"
            : "bg-white border-gray-200"
        }`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between p-4 border-b ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <FileText className="text-blue-400" size={20} />
            </div>
            <div>
              <h2
                className={`text-lg font-semibold ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Quick Quote
              </h2>
              <p
                className={`text-xs ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                For: {opportunity.name}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg ${
              isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
            }`}
          >
            <X size={20} />
          </button>
        </div>

        {/* Steps indicator */}
        <div
          className={`flex items-center px-6 py-3 border-b gap-4 ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          {[
            { n: 1, label: "Select Services" },
            { n: 2, label: "Customer & Terms" },
            { n: 3, label: "Review & Send" },
          ].map((s, i) => (
            <React.Fragment key={s.n}>
              <button
                onClick={() => setStep(s.n)}
                className="flex items-center gap-2"
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border-2 ${
                    step === s.n
                      ? "bg-blue-600 border-blue-600 text-white"
                      : step > s.n
                        ? "bg-green-600 border-green-600 text-white"
                        : isDarkMode
                          ? "bg-gray-800 border-gray-600 text-gray-400"
                          : "bg-gray-100 border-gray-300 text-gray-500"
                  }`}
                >
                  {step > s.n ? <CheckCircle size={16} /> : s.n}
                </div>
                <span
                  className={`text-sm font-medium hidden sm:inline ${
                    step === s.n
                      ? isDarkMode
                        ? "text-white"
                        : "text-gray-900"
                      : isDarkMode
                        ? "text-gray-400"
                        : "text-gray-500"
                  }`}
                >
                  {s.label}
                </span>
              </button>
              {i < 2 && (
                <div
                  className={`flex-1 h-0.5 ${
                    step > s.n
                      ? "bg-green-600"
                      : isDarkMode
                        ? "bg-gray-700"
                        : "bg-gray-300"
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="animate-spin" size={32} />
            </div>
          ) : (
            <>
              {/* Step 1: Select Services */}
              {step === 1 && (
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                  {/* Left: service catalog */}
                  <div className="lg:col-span-3 space-y-4">
                    <div className="flex gap-2 flex-wrap">
                      <div className="flex-1 min-w-0 relative">
                        <Search
                          size={16}
                          className={`absolute left-3 top-1/2 -translate-y-1/2 ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        />
                        <input
                          type="text"
                          placeholder="Search services..."
                          value={serviceSearch}
                          onChange={(e) => setServiceSearch(e.target.value)}
                          className={`w-full pl-9 pr-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-gray-800 border-gray-700 text-white"
                              : "bg-white border-gray-300 text-gray-900"
                          }`}
                        />
                      </div>
                      <select
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        className={`px-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 ${
                          isDarkMode
                            ? "bg-gray-800 border-gray-700 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        }`}
                      >
                        {categories.map((c) => (
                          <option key={c} value={c}>
                            {c === "all"
                              ? "All Categories"
                              : c.charAt(0).toUpperCase() + c.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[500px] overflow-y-auto pr-1">
                      {filteredServices.length === 0 ? (
                        <div
                          className={`col-span-full text-center py-8 rounded-lg border border-dashed ${
                            isDarkMode
                              ? "border-gray-700 text-gray-400"
                              : "border-gray-300 text-gray-500"
                          }`}
                        >
                          <Package
                            size={32}
                            className="mx-auto mb-2 opacity-40"
                          />
                          <p className="text-sm">No services found</p>
                        </div>
                      ) : (
                        filteredServices.map((service) => (
                          <button
                            key={service.id}
                            onClick={() => addService(service)}
                            className={`text-left p-3 rounded-lg border transition-all hover:border-blue-500 ${
                              isDarkMode
                                ? "bg-gray-800/50 border-gray-700"
                                : "bg-white border-gray-200"
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <div className="flex-1 min-w-0">
                                <h4
                                  className={`font-medium text-sm truncate ${
                                    isDarkMode ? "text-white" : "text-gray-900"
                                  }`}
                                >
                                  {service.name}
                                </h4>
                                <p
                                  className={`text-xs capitalize ${
                                    isDarkMode
                                      ? "text-gray-400"
                                      : "text-gray-500"
                                  }`}
                                >
                                  {service.category} •{" "}
                                  {service.billingCycle.replace("_", " ")}
                                </p>
                              </div>
                              <Plus
                                size={16}
                                className="text-blue-400 flex-shrink-0"
                              />
                            </div>
                            {service.description && (
                              <p
                                className={`text-xs line-clamp-2 mb-2 ${
                                  isDarkMode ? "text-gray-400" : "text-gray-500"
                                }`}
                              >
                                {service.description}
                              </p>
                            )}
                            {Array.isArray(service.termPrices) &&
                            service.termPrices.length > 0 ? (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {[...service.termPrices]
                                  .sort((a, b) => a.termMonths - b.termMonths)
                                  .map((tier) => (
                                    <span
                                      key={tier.termMonths}
                                      className="text-xs px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20"
                                    >
                                      {tier.termMonths}mo:{" "}
                                      {formatCurrency(tier.unitPrice)}
                                    </span>
                                  ))}
                              </div>
                            ) : (
                              <div className="text-sm font-semibold text-green-400">
                                {formatCurrency(service.unitPrice)}
                              </div>
                            )}
                          </button>
                        ))
                      )}
                    </div>

                    <button
                      onClick={addCustomItem}
                      className={`w-full px-4 py-2 rounded-lg border border-dashed transition-colors flex items-center justify-center gap-2 ${
                        isDarkMode
                          ? "border-gray-600 text-gray-400 hover:border-blue-500 hover:text-blue-400"
                          : "border-gray-300 text-gray-500 hover:border-blue-500 hover:text-blue-600"
                      }`}
                    >
                      <Plus size={16} />
                      Add custom line item
                    </button>
                  </div>

                  {/* Right: selected items */}
                  <div className="lg:col-span-2">
                    <h3
                      className={`text-sm font-semibold mb-3 ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      Selected ({lineItems.length})
                    </h3>
                    <div
                      className={`rounded-lg border p-3 space-y-2 max-h-[500px] overflow-y-auto ${
                        isDarkMode
                          ? "bg-gray-800/30 border-gray-700"
                          : "bg-gray-50 border-gray-200"
                      }`}
                    >
                      {lineItems.length === 0 ? (
                        <p
                          className={`text-sm text-center py-8 ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          No services selected yet
                        </p>
                      ) : (
                        lineItems.map((li, idx) => (
                          <div
                            key={idx}
                            className={`rounded-lg p-3 ${
                              isDarkMode ? "bg-gray-800" : "bg-white"
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <input
                                type="text"
                                value={li.name}
                                onChange={(e) =>
                                  updateLineItem(idx, "name", e.target.value)
                                }
                                className={`flex-1 bg-transparent text-sm font-medium focus:outline-none ${
                                  isDarkMode ? "text-white" : "text-gray-900"
                                }`}
                              />
                              <button
                                onClick={() => removeLineItem(idx)}
                                className="text-red-400 hover:text-red-300 p-1"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                            {/* Term selector — only shown when the service has term tiers */}
                            {Array.isArray(li.termPrices) &&
                              li.termPrices.length > 0 && (
                                <div className="mb-2">
                                  <label
                                    className={`text-xs ${
                                      isDarkMode
                                        ? "text-gray-400"
                                        : "text-gray-500"
                                    }`}
                                  >
                                    Contract Term
                                  </label>
                                  <select
                                    value={li.termMonths || ""}
                                    onChange={(e) => {
                                      const months = parseInt(e.target.value);
                                      const tier = li.termPrices.find(
                                        (t) =>
                                          parseInt(t.termMonths) === months,
                                      );
                                      // Update both fields atomically so neither
                                      // call sees stale state from the other.
                                      updateLineItemFields(idx, {
                                        termMonths: months,
                                        ...(tier && {
                                          unitPrice: parseFloat(tier.unitPrice),
                                        }),
                                      });
                                    }}
                                    className={`w-full px-2 py-1 rounded text-sm ${
                                      isDarkMode
                                        ? "bg-gray-700 text-white"
                                        : "bg-gray-100 text-gray-900"
                                    }`}
                                  >
                                    {li.termPrices
                                      .slice()
                                      .sort(
                                        (a, b) => a.termMonths - b.termMonths,
                                      )
                                      .map((tier) => (
                                        <option
                                          key={tier.termMonths}
                                          value={tier.termMonths}
                                        >
                                          {tier.termMonths} months —{" "}
                                          {formatCurrency(tier.unitPrice)}/unit
                                        </option>
                                      ))}
                                  </select>
                                </div>
                              )}
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label
                                  className={`text-xs ${
                                    isDarkMode
                                      ? "text-gray-400"
                                      : "text-gray-500"
                                  }`}
                                >
                                  Qty
                                </label>
                                <input
                                  type="number"
                                  min="1"
                                  value={li.quantity}
                                  onChange={(e) =>
                                    updateLineItem(
                                      idx,
                                      "quantity",
                                      e.target.value,
                                    )
                                  }
                                  className={`w-full px-2 py-1 rounded text-sm ${
                                    isDarkMode
                                      ? "bg-gray-700 text-white"
                                      : "bg-gray-100 text-gray-900"
                                  }`}
                                />
                              </div>
                              <div>
                                <label
                                  className={`text-xs ${
                                    isDarkMode
                                      ? "text-gray-400"
                                      : "text-gray-500"
                                  }`}
                                >
                                  Unit Price
                                  {Array.isArray(li.termPrices) &&
                                    li.termPrices.length > 0 && (
                                      <span className="ml-1 text-blue-400">
                                        (term rate)
                                      </span>
                                    )}
                                </label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={li.unitPrice}
                                  readOnly={
                                    Array.isArray(li.termPrices) &&
                                    li.termPrices.length > 0
                                  }
                                  onChange={(e) =>
                                    updateLineItem(
                                      idx,
                                      "unitPrice",
                                      e.target.value,
                                    )
                                  }
                                  className={`w-full px-2 py-1 rounded text-sm ${
                                    isDarkMode
                                      ? "bg-gray-700 text-white"
                                      : "bg-gray-100 text-gray-900"
                                  } ${
                                    Array.isArray(li.termPrices) &&
                                    li.termPrices.length > 0
                                      ? "opacity-60 cursor-not-allowed"
                                      : ""
                                  }`}
                                />
                              </div>
                            </div>
                            <div className="mt-2 text-right text-sm font-semibold text-green-400">
                              {formatCurrency(
                                li.quantity * li.unitPrice - (li.discount || 0),
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    {lineItems.length > 0 && (
                      <div
                        className={`mt-3 rounded-lg p-3 space-y-1 ${
                          isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
                        }`}
                      >
                        <div
                          className={`flex justify-between text-sm ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          <span>Subtotal</span>
                          <span>{formatCurrency(totals.subtotal)}</span>
                        </div>
                        <div
                          className={`flex justify-between text-sm ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          <span>Tax</span>
                          <span>{formatCurrency(totals.taxAmount)}</span>
                        </div>
                        <div
                          className={`flex justify-between text-base font-semibold pt-2 border-t ${
                            isDarkMode
                              ? "text-white border-gray-700"
                              : "text-gray-900 border-gray-300"
                          }`}
                        >
                          <span>Total</span>
                          <span className="text-green-400">
                            {formatCurrency(totals.total)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Step 2: Customer & Terms */}
              {step === 2 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    ["title", "Quote Title *", "text"],
                    ["customerName", "Customer Name *", "text"],
                    ["customerEmail", "Customer Email *", "email"],
                    ["customerCompany", "Customer Company", "text"],
                    ["customerPhone", "Customer Phone", "tel"],
                    ["validUntil", "Valid Until", "date"],
                  ].map(([field, label, type]) => (
                    <div key={field}>
                      <label
                        className={`block text-sm font-medium mb-1 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        {label}
                      </label>
                      <input
                        type={type}
                        value={formData[field] || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, [field]: e.target.value })
                        }
                        className={`w-full px-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 ${
                          isDarkMode
                            ? "bg-gray-800 border-gray-700 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        }`}
                      />
                    </div>
                  ))}
                  <div className="md:col-span-2">
                    <label
                      className={`block text-sm font-medium mb-1 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Description
                    </label>
                    <textarea
                      rows={2}
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        })
                      }
                      className={`w-full px-3 py-2 rounded-lg border resize-none focus:outline-none focus:border-blue-500 ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-700 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    />
                  </div>
                  <div>
                    <label
                      className={`block text-sm font-medium mb-1 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Discount
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="0"
                        value={formData.discountAmount}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            discountAmount: e.target.value,
                          })
                        }
                        className={`flex-1 px-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 ${
                          isDarkMode
                            ? "bg-gray-800 border-gray-700 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        }`}
                      />
                      <select
                        value={formData.discountType}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            discountType: e.target.value,
                          })
                        }
                        className={`px-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 ${
                          isDarkMode
                            ? "bg-gray-800 border-gray-700 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        }`}
                      >
                        <option value="fixed">
                          Fixed ({formData.currency})
                        </option>
                        <option value="percentage">Percentage (%)</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label
                      className={`block text-sm font-medium mb-1 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Currency
                    </label>
                    <select
                      value={formData.currency}
                      onChange={(e) =>
                        setFormData({ ...formData, currency: e.target.value })
                      }
                      className={`w-full px-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-700 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    >
                      <option value="GBP">GBP — British Pound</option>
                      <option value="USD">USD — US Dollar</option>
                      <option value="EUR">EUR — Euro</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label
                      className={`block text-sm font-medium mb-1 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Payment Terms
                    </label>
                    <input
                      type="text"
                      value={formData.paymentTerms}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          paymentTerms: e.target.value,
                        })
                      }
                      className={`w-full px-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-700 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label
                      className={`block text-sm font-medium mb-1 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Terms &amp; Conditions
                    </label>
                    <textarea
                      rows={4}
                      value={formData.termsAndConditions}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          termsAndConditions: e.target.value,
                        })
                      }
                      className={`w-full px-3 py-2 rounded-lg border resize-none focus:outline-none focus:border-blue-500 ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-700 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    />
                  </div>
                </div>
              )}

              {/* Step 3: Review */}
              {step === 3 && (
                <div className="space-y-4">
                  <div
                    className={`rounded-lg border p-4 ${
                      isDarkMode
                        ? "bg-gray-800/30 border-gray-700"
                        : "bg-gray-50 border-gray-200"
                    }`}
                  >
                    <h3
                      className={`font-semibold mb-3 ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {formData.title}
                    </h3>
                    <div
                      className={`grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      <div>
                        <span className="text-xs text-gray-500">Customer</span>
                        <p className="font-medium">{formData.customerName}</p>
                        <p className="text-xs">{formData.customerEmail}</p>
                      </div>
                      {formData.validUntil && (
                        <div>
                          <span className="text-xs text-gray-500">
                            Valid Until
                          </span>
                          <p className="font-medium">
                            {new Date(formData.validUntil).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div
                    className={`rounded-lg border overflow-hidden ${
                      isDarkMode
                        ? "bg-gray-800/30 border-gray-700"
                        : "bg-white border-gray-200"
                    }`}
                  >
                    <table className="w-full text-sm">
                      <thead>
                        <tr
                          className={`${
                            isDarkMode ? "bg-gray-800" : "bg-gray-100"
                          }`}
                        >
                          <th
                            className={`text-left px-3 py-2 ${
                              isDarkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            Service
                          </th>
                          <th
                            className={`text-right px-3 py-2 w-16 ${
                              isDarkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            Qty
                          </th>
                          <th
                            className={`text-right px-3 py-2 w-24 ${
                              isDarkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            Price
                          </th>
                          <th
                            className={`text-right px-3 py-2 w-24 ${
                              isDarkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            Total
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {lineItems.map((li, idx) => (
                          <tr
                            key={idx}
                            className={`border-t ${
                              isDarkMode ? "border-gray-700" : "border-gray-200"
                            }`}
                          >
                            <td
                              className={`px-3 py-2 ${
                                isDarkMode ? "text-white" : "text-gray-900"
                              }`}
                            >
                              <div>{li.name}</div>
                              {li.termMonths && (
                                <span className="inline-block mt-0.5 text-xs px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">
                                  {li.termMonths}-month term
                                </span>
                              )}
                            </td>
                            <td
                              className={`text-right px-3 py-2 ${
                                isDarkMode ? "text-gray-300" : "text-gray-700"
                              }`}
                            >
                              {li.quantity}
                            </td>
                            <td
                              className={`text-right px-3 py-2 ${
                                isDarkMode ? "text-gray-300" : "text-gray-700"
                              }`}
                            >
                              {formatCurrency(li.unitPrice)}
                            </td>
                            <td className="text-right px-3 py-2 font-semibold text-green-400">
                              {formatCurrency(
                                li.quantity * li.unitPrice - (li.discount || 0),
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div
                    className={`rounded-lg p-4 space-y-2 ${
                      isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
                    }`}
                  >
                    <div className="flex justify-between text-sm">
                      <span
                        className={
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }
                      >
                        Subtotal
                      </span>
                      <span
                        className={isDarkMode ? "text-white" : "text-gray-900"}
                      >
                        {formatCurrency(totals.subtotal)}
                      </span>
                    </div>
                    {totals.effectiveDiscount > 0 && (
                      <div className="flex justify-between text-sm text-orange-400">
                        <span>Discount</span>
                        <span>-{formatCurrency(totals.effectiveDiscount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span
                        className={
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }
                      >
                        Tax
                      </span>
                      <span
                        className={isDarkMode ? "text-white" : "text-gray-900"}
                      >
                        {formatCurrency(totals.taxAmount)}
                      </span>
                    </div>
                    <div
                      className={`flex justify-between text-lg font-bold pt-2 border-t ${
                        isDarkMode ? "border-gray-700" : "border-gray-300"
                      }`}
                    >
                      <span
                        className={isDarkMode ? "text-white" : "text-gray-900"}
                      >
                        Total
                      </span>
                      <span className="text-green-400">
                        {formatCurrency(totals.total)}
                      </span>
                    </div>
                    {totals.monthlyRecurring > 0 && (
                      <div
                        className={`text-xs pt-1 ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Includes {formatCurrency(totals.monthlyRecurring)} /
                        month recurring
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div
          className={`flex items-center justify-between p-4 border-t gap-3 ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg ${
              isDarkMode
                ? "bg-gray-700 hover:bg-gray-600"
                : "bg-gray-200 hover:bg-gray-300"
            }`}
          >
            Cancel
          </button>
          <div className="flex items-center gap-2">
            {step > 1 && (
              <button
                onClick={() => setStep(step - 1)}
                className={`px-4 py-2 rounded-lg ${
                  isDarkMode
                    ? "bg-gray-700 hover:bg-gray-600"
                    : "bg-gray-200 hover:bg-gray-300"
                }`}
              >
                Back
              </button>
            )}
            {step < 3 ? (
              <button
                onClick={() => setStep(step + 1)}
                disabled={step === 1 && lineItems.length === 0}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg flex items-center gap-2"
              >
                Next
              </button>
            ) : (
              <>
                <button
                  onClick={() => handleSave(false)}
                  disabled={saving}
                  className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                    isDarkMode
                      ? "bg-gray-700 hover:bg-gray-600"
                      : "bg-gray-200 hover:bg-gray-300"
                  } disabled:opacity-50`}
                >
                  {saving ? (
                    <Loader size={16} className="animate-spin" />
                  ) : (
                    <Save size={16} />
                  )}
                  Save as Draft
                </button>
                <button
                  onClick={() => handleSave(true)}
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? (
                    <Loader size={16} className="animate-spin" />
                  ) : (
                    <Send size={16} />
                  )}
                  Save &amp; Send
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickQuoteModal;
